import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress,
  Divider, Grid, IconButton, Stack, TableCell, TableRow, TextField,
  Typography, useMediaQuery, useTheme
} from '@mui/material';
import { Add, Delete as DeleteIcon, Edit as EditIcon, Refresh } from '@mui/icons-material';
import { reportCardAPI, academicAPI, studentAPI } from '../../services/api';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';
import { DataTable, TD_SX, useAppSnackbar, useConfirmDialog } from '../common/TableDesign';
import StudentConductFormDialog from './forms/StudentConductFormDialog';
import BulkConductEntry from './BulkConductEntry';

const extractCollection = (r) => {
  const p = r?.data !== undefined ? r.data : r;
  if (Array.isArray(p)) return p;
  if (Array.isArray(p?.data)) return p.data;
  if (Array.isArray(p?.data?.data)) return p.data.data;
  return [];
};
const err        = (e, fb = 'Operation failed') => e?.response?.data?.message || e?.response?.data?.error || fb;
const termLabel  = (t) => t ? `${t.name} ${t.year || ''}`.trim() : '';
const studName   = (s) => s?.full_name || `${s?.last_name || ''} ${s?.first_name || ''}`.trim() || s?.registration_no || '';
const catName    = (c) => c?.category_name || c?.name || '';
const ratingLabel = (r) => r ? `${r?.rating_name || r?.name || ''}${r?.rating_value != null ? ` (${r.rating_value})` : ''}` : '';
const acYear     = (t) => Number(t?.academic_year || t?.year || new Date().getFullYear());

const INITIAL_FORM = { student: null, term: null, rows: [] };
const COLUMNS      = ['Student', 'Class', 'Stream', 'Term', 'Category', 'Rating', 'Remarks', 'Actions'];
const PAGE_SIZE    = 15;

export default function StudentConduct() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { can } = useAccessControl();
  const canRead   = can(RESOURCES.REPORT_CARD.CONDUCT, ACTIONS.READ);
  const canCreate = can(RESOURCES.REPORT_CARD.CONDUCT, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.REPORT_CARD.CONDUCT, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.REPORT_CARD.CONDUCT, ACTIONS.DELETE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  // data
  const [records,     setRecords]     = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [ratings,     setRatings]     = useState([]);
  const [terms,       setTerms]       = useState([]);
  const [students,    setStudents]    = useState([]);
  const [classStreams, setClassStreams] = useState([]);

  // ui
  const [loadingBase,    setLoadingBase]    = useState(true);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [saving,         setSaving]         = useState(false);
  const [dialog,         setDialog]         = useState(false);
  const [editingRecord,  setEditingRecord]  = useState(null);
  const [form,           setForm]           = useState(INITIAL_FORM);
  const [bulkMode,       setBulkMode]       = useState(false);
  const [pagination,     setPagination]     = useState({ current_page: 1, last_page: 1, per_page: PAGE_SIZE, total: 0 });

  // filters
  const [termFilter,     setTermFilter]     = useState(null);
  const [classFilter,    setClassFilter]    = useState(null);
  const [streamFilter,   setStreamFilter]   = useState(null);
  const [studentFilter,  setStudentFilter]  = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [search,         setSearch]         = useState('');
  const [page,           setPage]           = useState(1);
  const [pageSize,       setPageSize]       = useState(PAGE_SIZE);

  // ── fetch ──────────────────────────────────────────────────────────────────
  const fetchBase = useCallback(async () => {
    setLoadingBase(true);
    try {
      const [catRes, ratRes, termRes, stuRes, csRes] = await Promise.all([
        reportCardAPI.getConductCategories(),
        reportCardAPI.getConductRatings(),
        academicAPI.getTerms(),
        studentAPI.getActive({ per_page: 2000 }),
        academicAPI.getClassStreamsForFilter(),
      ]);
      const termList = extractCollection(termRes);
      setCategories(extractCollection(catRes));
      setRatings(extractCollection(ratRes));
      setTerms(termList);
      setStudents(extractCollection(stuRes));
      setClassStreams(extractCollection(csRes));
      const current = termList.find((t) => t.is_current) || termList[0] || null;
      if (current) setTermFilter(current);
    } catch (e) {
      showSnackbar(err(e, 'Failed to load data'), 'error');
    } finally {
      setLoadingBase(false);
    }
  }, []); // eslint-disable-line

  const loadRecords = useCallback(async (p = page, ps = pageSize) => {
    setLoadingRecords(true);
    try {
      const params = { page: p, per_page: ps };
      if (studentFilter?.id)  params.student_id  = studentFilter.id;
      if (termFilter?.id)     { params.term_id = termFilter.id; params.academic_year = acYear(termFilter); }
      if (categoryFilter?.id) params.category_id = categoryFilter.id;
      const res     = await reportCardAPI.getStudentConduct(params);
      const payload = res?.data !== undefined ? res.data : res;
      setRecords(Array.isArray(payload?.data) ? payload.data : []);
      setPagination({
        current_page: payload?.current_page ?? p,
        last_page:    payload?.last_page    ?? 1,
        per_page:     payload?.per_page     ?? ps,
        total:        payload?.total        ?? 0,
      });
    } catch (e) {
      setRecords([]);
      showSnackbar(err(e, 'Failed to load conduct records'), 'error');
    } finally {
      setLoadingRecords(false);
    }
  }, [studentFilter, termFilter, categoryFilter]); // eslint-disable-line

  useEffect(() => { fetchBase(); }, [fetchBase]);
  useEffect(() => { setPage(1); loadRecords(1, pageSize); }, [loadRecords]); // eslint-disable-line

  // ── derived lists ──────────────────────────────────────────────────────────
  const classes = useMemo(() => {
    const seen = new Map();
    classStreams.forEach((cs) => {
      if (cs.class_id && !seen.has(cs.class_id))
        seen.set(cs.class_id, { id: cs.class_id, name: cs.name?.split('-')[0]?.trim() || `Class ${cs.class_id}` });
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [classStreams]);

  const streams = useMemo(() => {
    const pool = classFilter ? classStreams.filter((cs) => cs.class_id === classFilter.id) : classStreams;
    const seen = new Map();
    pool.forEach((cs) => {
      if (cs.stream_id && !seen.has(cs.stream_id))
        seen.set(cs.stream_id, { id: cs.stream_id, name: cs.stream_name || `Stream ${cs.stream_id}` });
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [classStreams, classFilter]);

  const matchingCsIds = useMemo(() => {
    if (!classFilter && !streamFilter) return null;
    return new Set(
      classStreams
        .filter((cs) => (!classFilter || cs.class_id === classFilter.id) && (!streamFilter || cs.stream_id === streamFilter.id))
        .map((cs) => cs.id)
    );
  }, [classStreams, classFilter, streamFilter]);

  const filteredStudents = useMemo(() => {
    if (!matchingCsIds) return students;
    return students.filter((s) => matchingCsIds.has(s.class_stream_id));
  }, [students, matchingCsIds]);

  const getStudentClass  = (s) => { const cs = classStreams.find((c) => c.id === s?.class_stream_id); return cs?.name?.split('-')[0]?.trim() || ''; };
  const getStudentStream = (s) => { const cs = classStreams.find((c) => c.id === s?.class_stream_id); return cs?.stream_name || ''; };

  const filtered = useMemo(() => {
    let list = records;
    if (matchingCsIds) list = list.filter((r) => matchingCsIds.has(r.student?.class_stream_id));
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) =>
      studName(r?.student).toLowerCase().includes(q) ||
      termLabel(r?.term).toLowerCase().includes(q)   ||
      catName(r?.category || r?.conduct_category).toLowerCase().includes(q) ||
      ratingLabel(r?.rating || r?.conduct_rating).toLowerCase().includes(q) ||
      String(r?.remarks || '').toLowerCase().includes(q)
    );
  }, [records, matchingCsIds, search]);

  // ── actions ────────────────────────────────────────────────────────────────
  const resetForm = () => { setForm(INITIAL_FORM); setEditingRecord(null); setDialog(false); };

  const handleAdd = () => {
    setForm({ ...INITIAL_FORM, term: termFilter || null });
    setDialog(true);
  };

  const handleEdit = (rec) => {
    setEditingRecord(rec);
    // In edit mode the dialog pre-fills the edited category row
    // other categories start empty
    setForm({
      student: rec.student || null,
      term:    rec.term    || null,
      rows:    [],  // dialog useEffect will populate from editingRecord + activeCategories
    });
    setDialog(true);
  };

  const handleDelete = (rec) => {
    confirm({
      title: 'Delete Conduct Record',
      message: `Delete conduct record for "${studName(rec.student)}"? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await reportCardAPI.deleteStudentConduct(rec.id);
          showSnackbar('Record deleted');
          loadRecords();
        } catch (e) { showSnackbar(err(e, 'Failed to delete'), 'error'); }
      },
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form.student?.id || !form.term?.id) {
      showSnackbar('Student and term are required', 'error'); return;
    }
    const filledRows = (form.rows || []).filter((r) => r.rating);
    if (filledRows.length === 0) {
      showSnackbar('Rate at least one conduct category', 'error'); return;
    }
    setSaving(true);
    try {
      if (editingRecord) {
        // editing a single record — find the row matching the edited category
        const editedCatId = editingRecord.category?.id || editingRecord.conduct_category?.id;
        const row = filledRows.find((r) => r.category.id === editedCatId) || filledRows[0];
        await reportCardAPI.updateStudentConduct(editingRecord.id, {
          conduct_category_id: row.category.id,
          conduct_rating_id:   row.rating.id,
          term_id:             form.term.id,
          academic_year:       acYear(form.term),
          remarks:             row.remarks || '',
        });
        // save any other filled rows as new records via bulkStore
        const otherRows = filledRows.filter((r) => r.category.id !== editedCatId);
        if (otherRows.length > 0) {
          await reportCardAPI.bulkCreateStudentConduct({
            term_id:       form.term.id,
            academic_year: acYear(form.term),
            conducts: otherRows.map((r) => ({
              student_id:          form.student.id,
              conduct_category_id: r.category.id,
              conduct_rating_id:   r.rating.id,
              remarks:             r.remarks || '',
            })),
          });
        }
        showSnackbar('Record updated');
      } else {
        await reportCardAPI.bulkCreateStudentConduct({
          term_id:       form.term.id,
          academic_year: acYear(form.term),
          conducts: filledRows.map((r) => ({
            student_id:          form.student.id,
            conduct_category_id: r.category.id,
            conduct_rating_id:   r.rating.id,
            remarks:             r.remarks || '',
          })),
        });
        showSnackbar(`${filledRows.length} conduct record${filledRows.length !== 1 ? 's' : ''} saved`);
      }
      resetForm();
      loadRecords();
    } catch (e) {
      showSnackbar(err(e, 'Failed to save record'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!canRead) return <Alert severity="error">You do not have permission to view conduct records.</Alert>;
  if (bulkMode) return <BulkConductEntry onBack={() => setBulkMode(false)} />;

  const total       = filtered.length;
  const lastPage    = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, lastPage);
  const paged       = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const paginationLocal = { current_page: currentPage, last_page: lastPage, per_page: pageSize, total };

  return (
    <Box>
      <ModulePageHeader
        title="Student Conduct Records"
        subtitle="Record and view student conduct per term"
        actions={[
          <Button key="bulk" variant="outlined" onClick={() => setBulkMode(true)} fullWidth={isMobile}>Bulk Entry</Button>,
          <Button key="refresh" variant="outlined" startIcon={<Refresh />}
            onClick={() => { fetchBase(); loadRecords(1, pageSize); }}
            disabled={loadingBase || loadingRecords} fullWidth={isMobile}>
            Refresh
          </Button>,
          canCreate && (
            <Button key="add" variant="contained" startIcon={<Add />}
              onClick={handleAdd} fullWidth={isMobile}>
              Add Record
            </Button>
          ),
        ].filter(Boolean)}
      />

      {/* ── Filters ── */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Filters</Typography>
          <Grid container spacing={2}>

            {/* Row 1: Term + Class + Stream */}
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete options={terms} value={termFilter}
                onChange={(_, v) => { setTermFilter(v); setPage(1); }}
                getOptionLabel={termLabel} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, t) => (
                  <Box component="li" {...props} key={t.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{termLabel(t)}</Typography>
                      {t.is_current && <Chip label="Current" size="small" color="success" />}
                    </Stack>
                  </Box>
                )}
                renderInput={(p) => <TextField {...p} label="Term" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete options={classes} value={classFilter}
                onChange={(_, v) => { setClassFilter(v); setStreamFilter(null); setStudentFilter(null); setPage(1); }}
                getOptionLabel={(c) => c.name} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Class" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete options={streams} value={streamFilter}
                onChange={(_, v) => { setStreamFilter(v); setStudentFilter(null); setPage(1); }}
                getOptionLabel={(s) => s.name} isOptionEqualToValue={(o, v) => o.id === v.id}
                disabled={streams.length === 0}
                renderInput={(p) => <TextField {...p} label="Stream" size="small" />}
              />
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            {/* Row 2: Student + Category + Search */}
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete options={filteredStudents} value={studentFilter}
                onChange={(_, v) => { setStudentFilter(v); setPage(1); }}
                getOptionLabel={studName} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, s) => (
                  <Box component="li" {...props} key={s.id}>
                    <Box>
                      <Typography variant="body2">{studName(s)}</Typography>
                      {s.registration_no && <Typography variant="caption" color="text.secondary">{s.registration_no}</Typography>}
                    </Box>
                  </Box>
                )}
                renderInput={(p) => <TextField {...p} label="Student" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete options={categories} value={categoryFilter}
                onChange={(_, v) => { setCategoryFilter(v); setPage(1); }}
                getOptionLabel={catName} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Category" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <TextField fullWidth size="small" label="Search"
                placeholder="Student, category, rating, remarks..."
                value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
            </Grid>

          </Grid>
        </CardContent>
      </Card>

      {/* ── Active filter chips ── */}
      {!loadingRecords && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip label={`${total} result${total !== 1 ? 's' : ''}`} size="small" />
          {termFilter     && <Chip label={termLabel(termFilter)}    size="small" color="primary"   variant="outlined" onDelete={() => setTermFilter(null)} />}
          {classFilter    && <Chip label={classFilter.name}         size="small" color="secondary" variant="outlined" onDelete={() => { setClassFilter(null); setStreamFilter(null); setStudentFilter(null); }} />}
          {streamFilter   && <Chip label={streamFilter.name}        size="small" color="secondary" variant="outlined" onDelete={() => { setStreamFilter(null); setStudentFilter(null); }} />}
          {categoryFilter && <Chip label={catName(categoryFilter)}  size="small" variant="outlined" onDelete={() => setCategoryFilter(null)} />}
          {studentFilter  && <Chip label={studName(studentFilter)}  size="small" variant="outlined" onDelete={() => setStudentFilter(null)} />}
        </Stack>
      )}

      {/* ── Results ── */}
      <Card>
        <CardContent>
          {loadingRecords ? (
            <Box py={6} textAlign="center"><CircularProgress size={28} /></Box>
          ) : filtered.length === 0 ? (
            <EmptyRecordsState message="No conduct records found." />
          ) : isMobile ? (
            <>
              <ResponsiveRecordList
                items={paged}
                renderItem={(rec) => (
                  <Stack spacing={0.75}>
                    <Typography fontWeight={700} variant="body2">{studName(rec.student)}</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {getStudentClass(rec.student)  && <Chip label={getStudentClass(rec.student)}  size="small" variant="outlined" />}
                      {getStudentStream(rec.student) && <Chip label={getStudentStream(rec.student)} size="small" variant="outlined" />}
                      <Chip label={termLabel(rec.term)} size="small" color="primary" variant="outlined" />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">{catName(rec.category || rec.conduct_category)}</Typography>
                    <Chip label={ratingLabel(rec.rating || rec.conduct_rating)} size="small" color="secondary" variant="outlined" sx={{ alignSelf: 'flex-start' }} />
                    {rec.remarks && <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{rec.remarks}</Typography>}
                    <Box textAlign="right">
                      {canUpdate && <IconButton size="small" color="primary" onClick={() => handleEdit(rec)}><EditIcon fontSize="small" /></IconButton>}
                      {canDelete && <IconButton size="small" color="error"   onClick={() => handleDelete(rec)}><DeleteIcon fontSize="small" /></IconButton>}
                    </Box>
                  </Stack>
                )}
              />
              <TablePagination pagination={paginationLocal} onPageChange={setPage}
                onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} isMobile />
            </>
          ) : (
            <>
              <DataTable columns={COLUMNS} data={paged} loading={false}
                emptyMessage="No conduct records found." minWidth={1000}
                renderRow={(rec) => (
                  <TableRow key={rec.id} hover>
                    <TableCell sx={TD_SX}>
                      <Typography variant="body2" fontWeight={600}>{studName(rec.student)}</Typography>
                      {rec.student?.registration_no && <Typography variant="caption" color="text.secondary">{rec.student.registration_no}</Typography>}
                    </TableCell>
                    <TableCell sx={TD_SX}><Typography variant="body2">{getStudentClass(rec.student)}</Typography></TableCell>
                    <TableCell sx={TD_SX}><Typography variant="body2">{getStudentStream(rec.student)}</Typography></TableCell>
                    <TableCell sx={TD_SX}><Chip label={termLabel(rec.term)} size="small" variant="outlined" /></TableCell>
                    <TableCell sx={TD_SX}><Typography variant="body2">{catName(rec.category || rec.conduct_category)}</Typography></TableCell>
                    <TableCell sx={TD_SX}><Chip label={ratingLabel(rec.rating || rec.conduct_rating)} size="small" color="secondary" variant="outlined" /></TableCell>
                    <TableCell sx={{ ...TD_SX, maxWidth: 220 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{rec.remarks || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={TD_SX} align="right">
                      {canUpdate && <IconButton size="small" color="primary" onClick={() => handleEdit(rec)}><EditIcon fontSize="small" /></IconButton>}
                      {canDelete && <IconButton size="small" color="error"   onClick={() => handleDelete(rec)}><DeleteIcon fontSize="small" /></IconButton>}
                    </TableCell>
                  </TableRow>
                )}
              />
              <TablePagination pagination={paginationLocal} onPageChange={setPage}
                onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} isMobile={isMobile} />
            </>
          )}
        </CardContent>
      </Card>

      <StudentConductFormDialog
        open={dialog} onClose={resetForm} onSubmit={handleSubmit}
        editingRecord={editingRecord} formData={form} setFormData={setForm}
        saving={saving} isMobile={isMobile}
        students={students} terms={terms}
        categories={categories} ratings={ratings}
        classStreams={classStreams}
      />
      {ConfirmDialogComponent}
      {SnackbarComponent}
    </Box>
  );
}
